/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 07-07-2026
 *
 * Bouwsuggestie-lampjes (noodzakelijk/gewenst) bij een node, met een ballon die de
 * regels van de `BouwsuggestieEngineService` afbeeldt. Vervangt de ng1
 * `bouwsuggestie`-directive. Sinds golf 8b leeft de engine in ng21: dit component
 * registreert zich er rechtstreeks bij (callbacks `updateNaRun`/`andereOpen`) en roept
 * verder alleen node-gekeyde engine-functies aan; alleen de log-/undo-/save-acties en de
 * sessie-gating lopen via het canvas (de acties door naar de samenwerk-baas).
 *
 * `bs_data` blijft in exact dezelfde vorm op de node geschreven als voorheen: dit
 * veld is geen vluchtige cache maar wordt via `node.data()` in het opgeslagen
 * model-JSON meegenomen (zie `ModelSerialisatie.schrijf()` in `model-serialisatie.ts`; in ng1
 * `modelData()` in `cytocanvas.modelview.controller.js`).
 */
import {ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject, signal} from '@angular/core';
import type {Core, NodeSingular} from 'cytoscape';
import {KeyedTimeout} from 'sihw-ngx';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {DlNodeDomBallonComponent} from '../dl-node-dom-ballon/dl-node-dom-ballon.component';
import {DlModelCanvasComponent} from '../../canvas/dl-modelcanvas/dl-modelcanvas.component';
import {BouwsuggestieEngineService} from '../../../app/engines/bouwsuggestie-engine.service';
import type {RegelInfo} from '../../../app/engines/bouwsuggestie-engine.service';
import {syncNodeHighlight} from '../../../lib/node-highlight';

/** Vaste vorm van `node.data('bs_data')` — moet exact overeenkomen met de ng1-versie (schema van het opgeslagen model). */
interface BsData {
    versie: number;
    numRegels: number;
    numNoodzakelijk: number;
    numGewenst: number;
    handlesNoodzakelijk: string[];
    handlesGewenst: string[];
    dlgOpen: boolean;
    regelstatus: 'rood' | 'oranje' | 'grijs';
    regelstatusNoodzakelijk: 'onverwerkt' | 'verwerkt';
    regelstatusGewenst: 'onverwerkt' | 'verwerkt';
    huidigeRegeluitleg: string;
    huidigeRegelHandle: string | undefined;
}

const BSDATA_VERSIE = 2;

@Component({
    selector: 'dl-bouwsuggestie',
    standalone: true,
    imports: [DlNodeDomBallonComponent, TranslateModule],
    templateUrl: './dl-bouwsuggestie.component.html',
    styleUrls: ['./dl-bouwsuggestie.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DlBouwsuggestieComponent implements OnInit, OnChanges, OnDestroy {

    /** De Cytoscape-node waarvoor bouwsuggesties getoond worden */
    @Input({required: true}) node!: NodeSingular;

    /** De Cytoscape-`Core` waarin de node leeft, voor de highlight-sync van relaties */
    @Input({required: true}) cy!: Core;

    /** Of noodzakelijke bouwsuggesties getoond mogen worden */
    @Input() noodzakelijkAan = true;

    /** Of gewenste bouwsuggesties getoond mogen worden */
    @Input() gewenstAan = true;

    /** Of het project noodzakelijke bouwsuggesties ondersteunt (voor de waarschuwingstekst bij gewenst) */
    @Input() projectOndersteuntNoodzakelijk = false;

    /** Meldt of de ballon open (true) of dicht (false) is, voor onderdrukking van de uitleg */
    @Output() subHover = new EventEmitter<boolean>();

    /** Of het noodzakelijk-lampje moet animeren (nieuwe regel verschenen) */
    protected readonly animeerNoodzakelijk = signal(false);

    /** Of het gewenst-lampje moet animeren (nieuwe regel verschenen) */
    protected readonly animeerGewenst = signal(false);

    /** Of de ballon open is (bron van waarheid voor de UI; wordt gespiegeld naar `bs_data`) */
    protected readonly dlgOpen = signal(false);

    /** De regel die momenteel in de ballon getoond wordt, of `undefined` */
    protected readonly huidigeRegelinfo = signal<RegelInfo | undefined>(undefined);

    /** Uitleg-/waarschuwingstekst bij de huidige regel */
    protected readonly huidigeRegeluitleg = signal('');

    /** Aantallen + handles per soort, voor de tellers en animatiedetectie */
    protected readonly aantallen = signal<{numNoodzakelijk: number; numGewenst: number; handlesNoodzakelijk: string[]; handlesGewenst: string[]}>(
        {numNoodzakelijk: 0, numGewenst: 0, handlesNoodzakelijk: [], handlesGewenst: []}
    );

    /** Verwerkt-status per soort, voor de `data-status`-attributen op de lampjes */
    protected readonly regelstatusNoodzakelijk = signal<'onverwerkt' | 'verwerkt'>('verwerkt');
    protected readonly regelstatusGewenst = signal<'onverwerkt' | 'verwerkt'>('verwerkt');

    private readonly modelcanvas = inject(DlModelCanvasComponent);
    private readonly bouwsuggestieEngine = inject(BouwsuggestieEngineService);
    private readonly translateService = inject(TranslateService);
    private readonly host: HTMLElement = inject(ElementRef).nativeElement;

    private dialoogregels: RegelInfo[] = [];
    private dialoogregelsgedaan: RegelInfo[] = [];
    private geopendVoorHard = false;
    private lokaalLockanimaties = false;

    private overLampje = false;
    private overDlg = false;
    private dialoogKnopGedrukt = false;

    /** De sluitvertraging van de ballon ('hoverclose') */
    private readonly timers = new KeyedTimeout();

    private laatsteHoverType: 'noodzakelijk' | 'gewenst' | null = null;

    private registratie: {meldDialoogGeopend: () => void; ontkoppel: () => void} | null = null;

    ngOnInit(): void {
        this.registratie = this.bouwsuggestieEngine.registreer(this.node, {
            updateNaRun: () => this.updateNaRun(),
            andereOpen: () => this.andereOpen()
        });
        this.initBsData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['noodzakelijkAan'] && !changes['noodzakelijkAan'].firstChange) {
            if (!this.noodzakelijkAan) this.animeerNoodzakelijk.set(false);
            this.herbereken();
            //weer aan, bijvoorbeeld na het sluiten van een simulatie: bij het uitgaan sloot de ballon
            //met de harde regel, en het geforceerd openen zit anders alleen in `updateNaRun`, dat pas
            //bij de volgende run van de engine draait — dus pas na een modelwijziging (testronde
            //20-09-2026). In de replay geen ballon, net als in `updateNaRun`
            if (this.noodzakelijkAan && !this.dlgOpen() && !this.modelcanvas.isInReplay()) {
                this.openVoorHardeRegel();
            }
        }
        if (changes['gewenstAan'] && !changes['gewenstAan'].firstChange) {
            if (!this.gewenstAan) this.animeerGewenst.set(false);
            this.herbereken();
        }
    }

    ngOnDestroy(): void {
        this.timers.clearAll();
        this.registratie?.ontkoppel();
    }

    /** Of het noodzakelijk-lampje getoond wordt */
    protected toonNoodzakelijk(): boolean {
        return this.noodzakelijkAan && this.aantallen().numNoodzakelijk > 0;
    }

    /** Of het gewenst-lampje getoond wordt */
    protected toonGewenst(): boolean {
        return this.gewenstAan && this.aantallen().numGewenst > 0;
    }

    /** Of er momenteel een harde regel in de ballon getoond wordt; blokkeert dan interactie met gewenst */
    protected isHardeRegelActief(): boolean {
        return this.dlgOpen() && this.toonNoodzakelijk() && this.huidigeRegelinfo()?.regel?.soort === 'hard';
    }

    /** Of interactie met het gewenst-lampje is toegestaan */
    protected magInteractieGewenst(): boolean {
        return !this.isHardeRegelActief();
    }

    /** Of het huidige model volledig readonly is (via het canvas) */
    protected fullReadonly(): boolean {
        return this.modelcanvas.fullReadonly();
    }

    /** Geeft het DOM-anker (lampje) terug waarop de ballon moet uitlijnen */
    protected getBouwsuggestieAnker = (): HTMLElement | null => {
        const lampjeVoor = (type: 'noodzakelijk' | 'gewenst'): HTMLElement | null => {
            const el = this.host.querySelector<HTMLElement>(`.bouwsuggestie.${type}`);
            return (el && el.offsetParent !== null) ? el : null;
        };
        let ankerType: 'noodzakelijk' | 'gewenst' | null = null;
        if (this.dlgOpen()) {
            const soort = this.huidigeRegelinfo()?.regel?.soort;
            if (this.toonNoodzakelijk() && (soort === 'hard' || soort === 'noodzakelijk')) {
                ankerType = 'noodzakelijk';
            } else if (this.toonGewenst() && soort === 'gewenst') {
                ankerType = 'gewenst';
            } else if (!this.huidigeRegelinfo() && this.laatsteHoverType) {
                ankerType = this.laatsteHoverType;
            }
        } else if (this.toonNoodzakelijk()) {
            ankerType = 'noodzakelijk';
        } else if (this.toonGewenst()) {
            ankerType = 'gewenst';
        }
        if (ankerType) {
            const el = lampjeVoor(ankerType);
            if (el) return el;
            const el2 = lampjeVoor(ankerType === 'noodzakelijk' ? 'gewenst' : 'noodzakelijk');
            if (el2) return el2;
        }
        return lampjeVoor('noodzakelijk') || lampjeVoor('gewenst');
    };

    /** Eventhandler voor mouseenter op een lampje */
    protected mouseInLampje(type: 'noodzakelijk' | 'gewenst'): void {
        if (this.modelcanvas.isInReplay()) return;
        this.overLampje = true;
        this.laatsteHoverType = type;
        this.timers.clear('hoverclose');
        if (!(type === 'gewenst' && this.projectOndersteuntNoodzakelijk && this.aantallen().numNoodzakelijk > 0)) {
            this.updateHuidigeRegel();
        }
        if (type === 'gewenst') {
            if (!this.magInteractieGewenst()) {
                this.stuurActie('sug_in');
                return;
            }
            if (this.projectOndersteuntNoodzakelijk && this.aantallen().numNoodzakelijk > 0) {
                const soort = this.huidigeRegelinfo()?.regel?.soort;
                if (this.dlgOpen() && this.dialoogKnopGedrukt && (soort === 'hard' || soort === 'noodzakelijk')) {
                    this.stuurActie('sug_in');
                    return;
                }
                const wasOpen = this.dlgOpen();
                if (!wasOpen) {
                    this.setDlgOpen(true);
                    this.registratie?.meldDialoogGeopend();
                    this.dialoogKnopGedrukt = false;
                }
                this.huidigeRegelinfo.set(undefined);
                this.huidigeRegeluitleg.set(this.translateService.instant('BOUWSUGGESTIE.EERST_RODE_REGELS'));
                this.saveSuggestiedata();
                this.stuurActie('sug_in');
            }
        }
        if (!this.dlgOpen()) this.openDlg(true);
        this.stuurActie('sug_in');
    }

    /** Eventhandler voor mouseleave op een lampje */
    protected mouseOutLampje(): void {
        this.overLampje = false;
        this.stuurActie('sug_uit');
        this.timers.set('hoverclose', () => {
            if (!this.overDlg && this.dlgOpen() && !this.dialoogKnopGedrukt) this.sluitDlg(false, false, true);
        }, 150);
    }

    /** Muis komt de ballon binnen */
    protected bijBallonIn(): void {
        this.overDlg = true;
        this.timers.clear('hoverclose');
    }

    /** Muis verlaat de ballon */
    protected bijBallonUit(): void {
        this.overDlg = false;
        this.timers.set('hoverclose', () => {
            if (!this.overLampje && this.dlgOpen() && !this.dialoogKnopGedrukt) this.sluitDlg(false, false, true);
        }, 150);
    }

    /** Klik op een lampje: opent of "lockt" de ballon, of sluit hem bij een tweede klik */
    protected klikLampje(): void {
        if (this.fullReadonly()) return;
        if (this.dlgOpen()) {
            if (!this.dialoogKnopGedrukt) {
                this.dialoogKnopGedrukt = true;
                this.stuurActie('sug_open', true, true);
                return;
            }
            this.stuurActie('sug_kliklamp');
            this.sluitDlg(true);
        } else {
            this.stuurActie('sug_kliklamp');
            this.openDlg();
        }
    }

    /** Sluitknop in de ballon */
    protected sluitDlgBtn(): void {
        if (this.fullReadonly()) return;
        this.dialoogKnopGedrukt = true;
        this.sluitDlg(true);
    }

    /** Regel wordt beantwoord (2 = ack, 3 = negeer) */
    protected beantwoordRegel(antwoord: 2 | 3): void {
        const huidigeRegelinfo = this.huidigeRegelinfo();
        if (this.modelcanvas.isInReplay() || this.fullReadonly() || !huidigeRegelinfo) return;
        this.dialoogKnopGedrukt = true;
        this.geopendVoorHard = false;

        let huidigeIndex = this.dialoogregels.indexOf(huidigeRegelinfo);
        let wasLaatste = false;

        const status = this.bouwsuggestieEngine.setRegelstatus(this.node, huidigeRegelinfo, antwoord);
        //handle mee in de args: de samenwerk-baas heeft hem nodig om het antwoord van een volger als
        //bsAntwoord-verzoek naar de leider te routeren (en hij maakt de actielog specifieker)
        const args = {antwoord, status, handle: huidigeRegelinfo.handle};

        if (status !== 1) {
            this.dialoogregelsgedaan.push(huidigeRegelinfo);
            if (this.dialoogregels.every(ri => this.dialoogregelsgedaan.includes(ri))) wasLaatste = true;
        }

        switch (status) {
            case 1:
                this.setHuidigeregelVoordialoog(0);
                break;
            case 2:
                this.dialoogregels.splice(huidigeIndex, 1);
                wasLaatste ? this.sluitDlg(false, true) : this.setHuidigeregelVoordialoog(huidigeIndex);
                break;
            case 3:
                this.dialoogregels.splice(huidigeIndex, 1);
                this.dialoogregels.push(huidigeRegelinfo);
                wasLaatste ? this.sluitDlg(false, true) : this.setHuidigeregelVoordialoog(huidigeIndex);
                break;
        }

        //alleen status 2 haalt de regel uit de set en is dus een modelwijziging (undo + save). Een
        //'nee' — en een 'ja' op een niet-ackbare regel, die de engine ook als 3 opslaat — verandert
        //alleen de volgorde in deze dialoog: die is per client, dus wel loggen, niet opslaan.
        this.stuurActie('sug_antwoord', status === 2, false, args);
    }

    /** Initialiseert `bs_data`: versie-migratie + eerste berekening, zoals de ng1 `init()` */
    private initBsData(): void {
        const heeftRegels = this.regelinfosVoor().length > 0;
        if (!heeftRegels) return;

        let bsdata = this.node.data('bs_data') as Partial<BsData> | undefined;
        if (!(bsdata && typeof bsdata === 'object' && Object.keys(bsdata).length > 0)) bsdata = {};
        const bsdataversie = bsdata.versie ?? 0;
        if (bsdataversie < BSDATA_VERSIE) {
            const nieuw: Record<string, unknown> = {...bsdata};
            if (bsdataversie < 2) delete nieuw['huidigeRegelinfo']; // legacy sleutel, ooit een hele regelinfo
            nieuw['versie'] = BSDATA_VERSIE;
            this.node.data('bs_data', nieuw);
        }

        this.aantallen.set(this.berekenAantallen());
        this.saveSuggestiedata();
    }

    /** Herberekent na een wijziging van de `noodzakelijkAan`/`gewenstAan`-inputs, zonder te animeren */
    private herbereken(): void {
        if (this.dlgOpen()) {
            this.lokaalLockanimaties = true;
            this.updateNaRun();
            this.lokaalLockanimaties = false;
        }
        syncNodeHighlight(this.node, this.cy, this, this.dlgOpen());
    }

    /** Zet de getallen en de verwerkt-status van de lampjes op wat de engine nu voor deze node heeft */
    private werkLampjesBij(): void {
        this.aantallen.set(this.berekenAantallen());
        this.regelstatusNoodzakelijk.set(this.berekenRegelstatusPerType(['hard', 'noodzakelijk']));
        this.regelstatusGewenst.set(this.berekenRegelstatusPerType(['gewenst']));
    }

    /** Engine meldt een run n.a.v. een gewijzigd model (dus niet n.a.v. beantwoorden van een regel) */
    private updateNaRun(): void {
        if (this.modelcanvas.isInReplay()) {
            //een replaystap is geen wijziging: geen animatie, geen ballon en geen `bs_data`. De lampjes
            //moeten wél bij de stap horen. Zonder dit hield een node die tussen twee stappen bleef staan
            //het getal van de stap ervoor, en klopte alleen een nieuw getekende node
            this.werkLampjesBij();
            return;
        }
        //de animatiedetectie hoort bij de run zelf, niet bij een van de twee takken hieronder: een
        //harde regel houdt de ballon geforceerd open, en dan kwam er nooit een lampje-animatie meer
        //(en werden de vorige handles ook nooit bijgewerkt). Hetzelfde gold voor een gewenste regel
        //die erbij kwam terwijl de noodzakelijke ballon open stond.
        this.checkAnimatie();
        if (this.dlgOpen()) {
            //vergelijken op handle én args, en nadrukkelijk niet op het hele regelinfo-object: daar
            //staat sinds de herbouw ook `isNieuw` in, en een regel die niet langer nieuw is zou dan
            //als een ándere regel lezen — waarna een openstaande ballon bij elke volgende run
            //herbouwd werd en zijn `sug_update` opnieuw logde
            const nieuweregels = this.bepaalDialoogregelsVoorSituatie();
            const gelijk = nieuweregels.length === this.dialoogregels.length &&
                nieuweregels.every(nri => this.dialoogregels.some(dri => nri.handle === dri.handle
                    && JSON.stringify(nri.args) === JSON.stringify(dri.args)));

            if (!gelijk) {
                // De volgorde in deze dialoog is van deze client: een 'nee' zet een regel achteraan
                // en dat reist niet over de samenwerklijn. We nemen de nieuwe regelinfo's over (hun
                // args kunnen gewijzigd zijn) maar houden onze eigen volgorde aan; regels die we nog
                // niet kenden komen achteraan, onderling in engine-volgorde (stabiele sort).
                const huidige = this.huidigeRegelinfo();
                const oudeVolgorde = this.dialoogregels.map(ri => ri.handle);
                const oudeIndex = huidige ? oudeVolgorde.indexOf(huidige.handle) : -1;
                const positie = (ri: RegelInfo): number => {
                    const i = oudeVolgorde.indexOf(ri.handle);
                    return i < 0 ? oudeVolgorde.length : i;
                };
                this.dialoogregels = nieuweregels.slice().sort((a, b) => positie(a) - positie(b));
                this.dialoogregelsgedaan = [];
                if (!this.dialoogregels.length) {
                    // niets meer te tonen: geforceerd sluiten, want de hard-regel-guard kijkt naar de
                    // regel die er net nog was en zou de ballon anders leeg laten hangen
                    this.sluitDlg(false, true);
                } else if (this.geopendVoorHard && this.dialoogregels[0].regel.soort !== 'hard') {
                    this.sluitDlg(false, true);
                } else {
                    // stond onze regel er nog, dan die; anders is de volgende op zijn plek geschoven
                    const nieuweIndex = huidige ? this.dialoogregels.findIndex(ri => ri.handle === huidige.handle) : -1;
                    this.setHuidigeregelVoordialoog(nieuweIndex >= 0
                        ? nieuweIndex
                        : Math.min(Math.max(oudeIndex, 0), this.dialoogregels.length - 1));
                }
            }
        } else {
            this.updateHuidigeRegel();
            this.openVoorHardeRegel();
        }
    }

    /**
     * Opent de ballon geforceerd als er voor deze node een harde regel ligt en de noodzakelijke
     * bouwsuggesties aan staan.
     */
    private openVoorHardeRegel(): void {
        const groepen = this.regelgroepenVoor();
        const heeftHardNood = !!groepen.noodzakelijk.find(ri => ri?.regel?.soort === 'hard');
        if (heeftHardNood && this.noodzakelijkAan) {
            this.geopendVoorHard = true;
            this.stuurActie('sug_forceer', false, false, {harde_regel: true});
            this.openDlg(false, true);
        }
    }

    /** Engine vraagt ons om te sluiten (een andere bouwsuggestie-dialoog is geopend) */
    private andereOpen(): void {
        if (this.dlgOpen() && !this.isHardeRegelActief()) this.sluitDlg();
    }

    /** Bepaalt en zet de huidige regel voor hover/inline weergave, en werkt `bs_data` bij */
    private updateHuidigeRegel(): void {
        if (this.modelcanvas.isInReplay()) return;
        const groepen = this.regelgroepenVoor();
        this.huidigeRegelinfo.set(groepen.noodzakelijk[0] ?? groepen.gewenst[0] ?? undefined);
        this.saveSuggestiedata();
    }

    /**
     * Laat een lampje pulseren als er een regel bij is gekomen die er de vorige run nog niet was.
     * Alleen de lampjes-animatie; de "nieuwe dingen"-ballon in de feedbackbalk loopt sinds de
     * 3t-testronde via de tellersignalen in het eiland zelf (race-vrij bij leider én volger).
     *
     * Wát er nieuw is staat sinds de herbouw van de "is nieuw"-bepaling op de regel zelf: de engine
     * zet het op het moment dat de uitkomst binnenkomt, en bij een volger komt het antwoord van de
     * leider mee. Dit component telt dus niets — dat ging mis zodra het bij een hertekening vers
     * begon terwijl de uitkomst van vóór die hertekening stamde.
     */
    private checkAnimatie(): void {
        const aantallen = this.berekenAantallen();
        this.aantallen.set(aantallen);
        if (this.lokaalLockanimaties || this.zijnAnimatiesGeblokkeerd()) {
            return;
        }
        const groepen = this.regelgroepenVoor();
        if (groepen.noodzakelijk.some(regelinfo => regelinfo.isNieuw)) {
            this.animeerNoodzakelijk.set(this.noodzakelijkAan);
        }
        if (groepen.gewenst.some(regelinfo => regelinfo.isNieuw)) {
            this.animeerGewenst.set(this.gewenstAan);
        }
    }

    /** Zet de dialoog op open, en laat de engine dit regelen met de andere geregistreerde nodes */
    private openDlg(vanHover?: boolean, geforceerd?: boolean): void {
        if (this.dlgOpen()) return;

        this.dialoogregels = this.bepaalDialoogregelsVoorSituatie();
        this.dialoogregelsgedaan = [];
        if (!this.dialoogregels.length) return;
        if (!this.setHuidigeregelVoordialoog(0)) return;

        // pas nu, ná bevestiging dat er echt iets te tonen is, andere bouwsuggestie-dialogen laten sluiten
        this.registratie?.meldDialoogGeopend();
        this.dialoogKnopGedrukt = false;
        this.setDlgOpen(true);
        const vis = this.saveSuggestiedata();
        if (!vanHover) this.stuurActie('sug_open', !geforceerd, vis);
    }

    /** Sluit de dialoog zonder verdere actie, tenzij een harde regel dat blokkeert */
    private sluitDlg(handmatig?: boolean, forceer?: boolean, vanHover?: boolean): void {
        if (!forceer && this.isHardeRegelActief()) {
            if (handmatig) this.stuurActie('sug_stop_close');
            return;
        }

        this.setDlgOpen(false);
        this.overDlg = false;
        this.timers.clear('hoverclose');
        const vis = this.saveSuggestiedata();
        if (!vanHover) this.stuurActie(handmatig ? 'sug_close' : 'sug_close_auto', handmatig, vis);
        this.geopendVoorHard = false;
        this.updateHuidigeRegel();
    }

    /** Vult de huidigeregel-variabelen met de regel op de opgegeven index in de dialoogregels */
    private setHuidigeregelVoordialoog(index: number): boolean {
        const nieuweregel = this.dialoogregels[index];
        if (!nieuweregel) {
            //geen regel meer op deze plek: geforceerd sluiten, anders houdt de hard-regel-guard de
            //dan lege ballon open (de vorige regel staat nog in huidigeRegelinfo)
            this.sluitDlg(false, true);
            return false;
        }
        this.huidigeRegelinfo.set(nieuweregel);
        const vis = this.saveSuggestiedata();
        this.stuurActie('sug_update', false, vis);
        return true;
    }

    /** Zet `dlgOpen` (bron van waarheid voor de UI) en synchroniseert de node-highlight */
    private setDlgOpen(open: boolean): void {
        this.dlgOpen.set(open);
        this.subHover.emit(open);
        syncNodeHighlight(this.node, this.cy, this, open);
    }

    /**
     * Stuurt een actie-event naar het canvas, dat 'm doorzet naar de samenwerk-baas (loggen/undo/opslaan).
     *
     * `soort` is de rauwe regelsoort, zodat het rood/grijs-onderscheid in de actielogdata zichtbaar
     * is (`hard` en `noodzakelijk` zijn rood, `gewenst` is grijs). Zonder regel — de "eerst de rode
     * regels"-melding zet `huidigeRegelinfo` bewust op `undefined` — valt hij terug op het lampje
     * waar de muis op staat; is ook dat er niet, dan blijft het veld gewoon weg.
     */
    private stuurActie(type: string, wijziging?: boolean, visuelewijziging?: boolean, extraargs?: Record<string, unknown>): void {
        if (this.fullReadonly()) return;
        this.modelcanvas.bouwsuggestieActie({
            type,
            node: this.node,
            wijziging: !!wijziging,
            visuelewijziging: !!visuelewijziging,
            open: this.dlgOpen(),
            args: {
                regel: this.huidigeRegelinfo()?.handle,
                soort: this.huidigeRegelinfo()?.regel?.soort ?? this.laatsteHoverType ?? undefined,
                feedback: this.huidigeRegeluitleg(),
                ...(extraargs ?? {})
            }
        });
    }

    /** Bepaalt de set dialoogregels voor de huidige situatie: eerst noodzakelijk, anders gewenst */
    private bepaalDialoogregelsVoorSituatie(): RegelInfo[] {
        const groepen = this.regelgroepenVoor();
        if (this.noodzakelijkAan && groepen.noodzakelijk.length > 0) return groepen.noodzakelijk.slice();
        if (this.gewenstAan && groepen.gewenst.length > 0) return groepen.gewenst.slice();
        return [];
    }

    /** Telt het aantal bouwsuggestieregels per soort en verzamelt de handles */
    private berekenAantallen(): {numNoodzakelijk: number; numGewenst: number; handlesNoodzakelijk: string[]; handlesGewenst: string[]} {
        const groepen = this.regelgroepenVoor();
        const handlesNoodzakelijk = groepen.noodzakelijk.map(ri => ri.handle);
        const handlesGewenst = groepen.gewenst.map(ri => ri.handle);
        return {
            numNoodzakelijk: handlesNoodzakelijk.length,
            numGewenst: handlesGewenst.length,
            handlesNoodzakelijk,
            handlesGewenst
        };
    }

    /** Berekent de globale (deprecated) regelstatus: rood als een harde/noodzakelijke regel onverwerkt is, anders oranje/grijs */
    private berekenRegelstatus(): 'rood' | 'oranje' | 'grijs' {
        const regelstatus = this.regelstatusMap();
        const regelinfos = this.regelinfosVoor();
        if (regelinfos.some(ri => ['hard', 'noodzakelijk'].includes(ri.regel.soort) && regelstatus[ri.handle] === 3)) return 'rood';
        if (regelinfos.some(ri => ri.regel.soort === 'gewenst' && regelstatus[ri.handle] === 3)) return 'oranje';
        return 'grijs';
    }

    /** Berekent de verwerkt-status voor de opgegeven regelsoorten */
    private berekenRegelstatusPerType(soorten: string[]): 'onverwerkt' | 'verwerkt' {
        const regelstatus = this.regelstatusMap();
        const relevante = this.regelinfosVoor().filter(ri => soorten.includes(ri.regel.soort));
        return relevante.some(ri => regelstatus[ri.handle] === 3) ? 'onverwerkt' : 'verwerkt';
    }

    /** Zoekt de vertaalde uitlegtekst op voor een regel, met de betrokken node-labels als argumenten */
    private regelTekst(regelinfo: RegelInfo): string {
        const naam = (data: {label?: string; nodename?: string} | undefined): string =>
            data?.label?.length ? data.label : (data?.nodename?.length ? data.nodename : '?');
        const args: Record<string, string> = {label: naam(this.node.data())};
        let teller = 2;
        for (const arg of regelinfo.args) {
            args[`arg${teller}`] = naam(this.cy.$id(arg)?.data());
            teller++;
        }
        const regelcode = `BOUWSUGGESTIE.REGEL.${regelinfo.handle.toUpperCase()}`;
        let uitleg = this.translateService.instant(regelcode + '.UITLEG', args);
        if (uitleg === regelcode + '.UITLEG') uitleg = this.translateService.instant(regelcode + '.FEEDBACK', args);
        return uitleg;
    }

    /** Slaat `bs_data` op in exact dezelfde vorm als de ng1-directive. Geeft terug of er iets wijzigde. */
    private saveSuggestiedata(): boolean {
        if (this.modelcanvas.isInReplay() || this.fullReadonly()) return false;

        const huidigeRegelinfo = this.huidigeRegelinfo();
        const uitleg = huidigeRegelinfo ? (this.regelTekst(huidigeRegelinfo) || '') : this.huidigeRegeluitleg();
        const aantallen = this.berekenAantallen();
        this.aantallen.set(aantallen);
        const regelstatusNoodzakelijk = this.berekenRegelstatusPerType(['hard', 'noodzakelijk']);
        const regelstatusGewenst = this.berekenRegelstatusPerType(['gewenst']);
        this.regelstatusNoodzakelijk.set(regelstatusNoodzakelijk);
        this.regelstatusGewenst.set(regelstatusGewenst);
        this.huidigeRegeluitleg.set(uitleg);

        const suggestiedata: BsData = {
            versie: BSDATA_VERSIE,
            numRegels: aantallen.numNoodzakelijk + aantallen.numGewenst,
            numNoodzakelijk: aantallen.numNoodzakelijk,
            numGewenst: aantallen.numGewenst,
            handlesNoodzakelijk: aantallen.handlesNoodzakelijk,
            handlesGewenst: aantallen.handlesGewenst,
            dlgOpen: this.dlgOpen(),
            regelstatus: this.berekenRegelstatus(),
            regelstatusNoodzakelijk,
            regelstatusGewenst,
            huidigeRegeluitleg: uitleg,
            huidigeRegelHandle: huidigeRegelinfo?.handle
        };
        const huidig = this.node.data('bs_data');
        if (huidig && JSON.stringify(huidig) === JSON.stringify(suggestiedata)) return false;
        this.node.data('bs_data', suggestiedata);
        return true;
    }

    /** Alle regelinfo's (noodzakelijk + gewenst) voor deze node, uit de engine */
    private regelinfosVoor(): RegelInfo[] {
        const groepen = this.regelgroepenVoor();
        return [...groepen.noodzakelijk, ...groepen.gewenst];
    }

    /** De regelgroepen (noodzakelijk/gewenst) voor deze node, uit de engine */
    private regelgroepenVoor(): {noodzakelijk: RegelInfo[]; gewenst: RegelInfo[]} {
        return this.bouwsuggestieEngine.regelgroepenVoor(this.node);
    }

    /** De regelstatus-map (handle → 2 of 3) voor deze node; engine-eigendom, rechtstreeks uit `node.data('bs')` */
    private regelstatusMap(): Record<string, 2 | 3> {
        return (this.node.data('bs')?.rs as Record<string, 2 | 3>) ?? {};
    }

    /** Of animaties op dit moment onderdrukt moeten worden (bv. tijdens het laden van een model) */
    private zijnAnimatiesGeblokkeerd(): boolean {
        return this.modelcanvas.zijnAnimatiesGeblokkeerd();
    }
}
